import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { menuPermissionsApi } from '../services/api'
import { OnlineUsersWidget } from '../components/OnlineUsersWidget'
import {
  ClipboardList, Calendar, Map, Package, FileText, Receipt, LayoutGrid,
  LayoutDashboard, Truck, Briefcase, CalendarCheck, Car, CreditCard,
  BarChart2, Users, BarChart3, TrendingUp, MessageCircle, type LucideIcon,
  ArrowLeftRight, SlidersHorizontal, Settings, Building2, ListChecks, Clock, FileEdit,
  Vote, HardDrive, CalendarClock, FileCheck, Calculator, FileSpreadsheet,
} from 'lucide-react'

interface Tile {
  id: string
  label: string
  icon: LucideIcon
  route: string
  color: [string, string]
  roles?: string[]
}

interface Section {
  title: string
  tiles: Tile[]
}

const STAFF_ROLES = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico', 'readonly']
const TECH_ROLES  = [...STAFF_ROLES, 'tecnico_senior', 'tecnico']

const SECTIONS: Section[] = [
  {
    title: 'Delta Poste',
    tiles: [
      { id: 'pratiche',    label: 'Pratiche',      icon: ClipboardList,   route: '/pratiche',    color: ['#FF6B35', '#E63946'] },
      { id: 'calendario',  label: 'Calendario',    icon: Calendar,        route: '/calendario',  color: ['#7B2FBE', '#4361EE'] },
      { id: 'mappa',       label: 'Mappa',         icon: Map,             route: '/mappa',       color: ['#06D6A0', '#118AB2'] },
      { id: 'rapportini',  label: 'Rapportini',    icon: FileText,        route: '/rapportini',  color: ['#F72585', '#B5179E'] },
      { id: 'dispatcher',  label: 'Dispatcher',    icon: LayoutGrid,      route: '/dispatcher',  color: ['#FFB703', '#FB8500'], roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'readonly'] },
      { id: 'dashboard',   label: 'Dashboard KPI', icon: LayoutDashboard, route: '/dashboard',   color: ['#4CC9F0', '#4361EE'], roles: ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico', 'contabilita', 'readonly'] },
      { id: 'cambio_porte', label: 'Cambio Porte', icon: ArrowLeftRight, route: '/cambio-porte', color: ['#8ECAE6', '#219EBC'] },
    ],
  },
  {
    title: 'Magazzino & Risorse',
    tiles: [
      { id: 'magazzino',    label: 'Magazzino',    icon: Package,       route: '/magazzino',               color: ['#FFB703', '#FB8500'], roles: TECH_ROLES },
      { id: 'fleet',        label: 'Parco Auto',   icon: Truck,         route: '/fleet',                   color: ['#06D6A0', '#118AB2'], roles: STAFF_ROLES },
      { id: 'commesse',     label: 'Commesse',     icon: Briefcase,     route: '/commesse',                color: ['#7B2FBE', '#4361EE'], roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'contabilita'] },
      { id: 'assegnazioni', label: 'Assegnazioni', icon: CalendarCheck, route: '/calendario-assegnazioni', color: ['#FF6B35', '#E63946'] },
    ],
  },
  {
    title: 'IMECO',
    tiles: [
      { id: 'trasferte',          label: 'Trasferte',        icon: Car,        route: '/trasferte',          color: ['#FF6B35', '#E63946'] },
      { id: 'spese_rimborso',     label: 'Spese & Rimborsi', icon: CreditCard, route: '/spese-rimborso',     color: ['#7B2FBE', '#4361EE'], roles: ['admin', 'superadmin', 'responsabile_tecnico', 'contabilita', 'tecnico', 'tecnico_senior'] },
      { id: 'gestione_personale', label: 'Gest. Personale',  icon: Users,      route: '/gestione-personale', color: ['#06D6A0', '#118AB2'], roles: STAFF_ROLES },
      { id: 'report_trasferte',   label: 'Report Trasferte', icon: BarChart2,  route: '/trasferte/report',   color: ['#F72585', '#B5179E'], roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'contabilita', 'readonly'] },
    ],
  },
  {
    title: 'Report & Produzione',
    tiles: [
      { id: 'fatturazione',     label: 'Produzione / SAL', icon: Receipt,       route: '/fatturazione',     color: ['#06D6A0', '#118AB2'], roles: ['admin', 'superadmin', 'contabilita', 'responsabile_tecnico', 'readonly'] },
      { id: 'report',           label: 'Report',           icon: BarChart3,     route: '/report',           color: ['#4CC9F0', '#4361EE'], roles: [...STAFF_ROLES, 'contabilita'] },
      { id: 'report_economico', label: 'Report Economico', icon: TrendingUp,    route: '/report-economico', color: ['#FFB703', '#FB8500'], roles: ['admin', 'superadmin', 'contabilita', 'responsabile_tecnico'] },
      { id: 'whatsapp',         label: 'WhatsApp',         icon: MessageCircle, route: '/whatsapp',         color: ['#7B2FBE', '#4361EE'], roles: STAFF_ROLES },
    ],
  },
  {
    title: 'Configurazione',
    tiles: [
      { id: 'impostazioni',          label: 'Impostazioni',       icon: SlidersHorizontal, route: '/impostazioni',          color: ['#495057', '#212529'], roles: ['superadmin', 'admin'] },
      { id: 'utenti',                label: 'Utenti',              icon: Settings,          route: '/utenti',                color: ['#6C757D', '#343A40'], roles: ['superadmin', 'admin', 'readonly'] },
      { id: 'committenti',           label: 'Committenti',         icon: Building2,         route: '/committenti',           color: ['#118AB2', '#073B4C'], roles: STAFF_ROLES },
      { id: 'clienti',               label: 'Clienti Finali',      icon: Users,             route: '/clienti',               color: ['#06D6A0', '#073B4C'], roles: STAFF_ROLES },
      { id: 'checklist',             label: 'Checklist',           icon: ListChecks,        route: '/checklist',             color: ['#FFB703', '#023047'], roles: ['admin', 'superadmin', 'dispatcher', 'readonly'] },
      { id: 'orari_apertura',        label: 'Orari Apertura',      icon: Clock,             route: '/orari-apertura',        color: ['#FB8500', '#023047'], roles: ['admin', 'superadmin', 'dispatcher', 'readonly'] },
      { id: 'rapportino_templates',  label: 'Template Rapportini', icon: FileEdit,          route: '/rapportino-templates',  color: ['#F72585', '#7209B7'], roles: ['superadmin', 'admin'] },
      { id: 'rlsvote',               label: 'RLS Votazione',       icon: Vote,              route: '/rlsvote-crea-elezione', color: ['#7209B7', '#3A0CA3'], roles: ['superadmin', 'admin'] },
      { id: 'backup',                label: 'Backup',              icon: HardDrive,         route: '/backup',                color: ['#343A40', '#000000'], roles: ['superadmin'] },
    ],
  },
  {
    title: 'RED — Gestione Immobiliare',
    tiles: [
      { id: 'red_dashboard',    label: 'Dashboard RED',       icon: BarChart3,     route: '/red/dashboard',      color: ['#7B2FBE', '#3A0CA3'], roles: STAFF_ROLES },
      { id: 'red_portfolio',    label: 'Portfolio Immobili',  icon: Building2,     route: '/red',                color: ['#4361EE', '#3A0CA3'], roles: STAFF_ROLES },
      { id: 'red_mappa',        label: 'Mappa Immobili',      icon: Map,           route: '/red/mappa',          color: ['#4CC9F0', '#4895EF'], roles: STAFF_ROLES },
      { id: 'red_gantt',        label: 'Cronoprogramma',      icon: Calendar,      route: '/red/gantt',          color: ['#F72585', '#7209B7'], roles: STAFF_ROLES },
      { id: 'red_opportunita',  label: 'Pipeline Opportunità', icon: Briefcase,    route: '/red/opportunita',    color: ['#FF6B35', '#E63946'], roles: STAFF_ROLES },
      { id: 'red_professionisti', label: 'Rubrica Professionisti', icon: Users,    route: '/red/professionisti', color: ['#118AB2', '#073B4C'], roles: STAFF_ROLES },
      { id: 'red_pratiche_usr', label: 'Pratiche USR',        icon: FileText,      route: '/red/pratiche-usr',   color: ['#FFB703', '#FB8500'], roles: STAFF_ROLES },
      { id: 'red_agenda',       label: 'Agenda RED',          icon: CalendarClock, route: '/red/agenda',         color: ['#06D6A0', '#118AB2'], roles: STAFF_ROLES },
      { id: 'red_impostazioni', label: 'Impostazioni RED',    icon: Settings,      route: '/red/impostazioni',   color: ['#6C757D', '#343A40'], roles: STAFF_ROLES },
    ],
  },
  {
    title: 'NICI',
    tiles: [
      { id: 'nici_dashboard', label: 'Dashboard NICI',       icon: LayoutDashboard, route: '/nici',         color: ['#4361EE', '#3A0CA3'], roles: STAFF_ROLES },
      { id: 'timsurvey',      label: 'Pratiche Sopralluogo', icon: ClipboardList,   route: '/timsurvey',    color: ['#FF6B35', '#E63946'], roles: [...STAFF_ROLES, 'tecnico_senior', 'tecnico'] },
      { id: 'nici_offerte',   label: 'Offerte / Computi',    icon: Calculator,      route: '/nici/offerte', color: ['#06D6A0', '#118AB2'], roles: [...STAFF_ROLES, 'contabilita'] },
      { id: 'nici_listini',   label: 'Listini',              icon: FileSpreadsheet, route: '/nici/listini', color: ['#FFB703', '#FB8500'], roles: STAFF_ROLES },
    ],
  },
  {
    title: 'RFI',
    tiles: [
      { id: 'rfi_ordini',        label: 'Ordini di Lavoro', icon: FileText,  route: '/rfi',              color: ['#7B2FBE', '#4361EE'], roles: STAFF_ROLES },
      { id: 'rfi_rapportini',    label: 'Rapportini RFI',   icon: FileCheck, route: '/rfi/rapportini',   color: ['#F72585', '#B5179E'], roles: STAFF_ROLES },
      { id: 'rfi_listino',       label: 'Listino Prezzi',   icon: FileText,  route: '/rfi/listino',      color: ['#4CC9F0', '#4361EE'], roles: STAFF_ROLES },
      { id: 'rfi_impostazioni',  label: 'Impostazioni RFI', icon: Settings,  route: '/rfi/impostazioni', color: ['#6C757D', '#343A40'], roles: STAFF_ROLES },
    ],
  },
]

function TileCard({ tile }: { tile: Tile }) {
  const Icon = tile.icon
  return (
    <Link
      to={tile.route}
      className="home-tile"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        borderRadius: 14,
        background: `linear-gradient(135deg, ${tile.color[0]}, ${tile.color[1]})`,
        boxShadow: `0 3px 10px ${tile.color[1]}44`,
        textDecoration: 'none',
        transition: 'transform 0.14s, box-shadow 0.14s',
        cursor: 'pointer',
        padding: '10px 6px',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLElement
        el.style.transform = 'translateY(-2px) scale(1.025)'
        el.style.boxShadow = `0 8px 20px ${tile.color[1]}66`
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLElement
        el.style.transform = 'none'
        el.style.boxShadow = `0 3px 10px ${tile.color[1]}44`
      }}
    >
      <Icon size={26} color="white" strokeWidth={1.7} style={{ flexShrink: 0 }} />
      <span style={{
        color: 'white',
        fontSize: 11,
        fontWeight: 600,
        textAlign: 'center',
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
        textShadow: '0 1px 3px rgba(0,0,0,0.25)',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '0 2px',
      }}>
        {tile.label}
      </span>
    </Link>
  )
}

export default function HomeDashboard() {
  const { user } = useAuth()

  const { data: myMenus } = useQuery({
    queryKey: ['my-menus', user?.id],
    queryFn: () => menuPermissionsApi.myMenus().then(r => r.data as { id: string; route: string }[]),
    enabled: !!user,
    staleTime: 60_000,
  })

  const isVisible = (tile: Tile) => {
    const roleOk = !tile.roles || tile.roles.includes(user?.role ?? '')
    if (myMenus && myMenus.length > 0) return myMenus.some(m => m.route === tile.route) && roleOk
    return roleOk
  }

  const visibleSections = SECTIONS
    .map(s => ({ ...s, tiles: s.tiles.filter(isVisible) }))
    .filter(s => s.tiles.length > 0)

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera'

  return (
    <>
      <style>{`
        .home-root {
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-sizing: border-box;
        }
        .home-sections {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .home-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .home-tiles-row {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
          gap: 8px;
        }
        .home-tile {
          height: 84px;
          min-width: 0;
        }
        @media (max-width: 640px) {
          .home-tiles-row {
            grid-template-columns: repeat(2, 1fr);
          }
          .home-tile {
            height: 90px;
          }
        }
        @media (max-width: 400px) {
          .home-tile {
            height: 80px;
          }
        }
      `}</style>

      <div className="home-root">
        {/* Header */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h1 style={{
              fontSize: '1.2rem',
              fontWeight: 800,
              color: 'var(--mac-label)',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              margin: 0,
            }}>
              {greeting}, {user?.nome}
            </h1>
            <p style={{ fontSize: 12, color: 'var(--mac-label-2)', margin: '2px 0 0', fontWeight: 400 }}>
              Cosa vuoi fare oggi?
            </p>
          </div>
          <OnlineUsersWidget />
        </div>

        {/* Sezioni — griglia responsiva, le tile vanno a capo in base allo spazio disponibile */}
        <div className="home-sections">
          {visibleSections.map(section => (
            <div key={section.title} className="home-section">
              <div style={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase' as const,
                color: 'var(--mac-label-3)',
                flexShrink: 0,
                lineHeight: 1,
              }}>
                {section.title}
              </div>
              <div className="home-tiles-row">
                {section.tiles.map(tile => (
                  <TileCard key={tile.id} tile={tile} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
